import { ProtectedNotificationHours } from "./notification-hours";
import type { AdministrationSelfContext } from "@reminder-tracker/backend-contracts";
import type { AdministrationPageId } from "@reminder-tracker/domain";
import { useQuery } from "convex/react";
import { Component, useState, type ReactNode } from "react";

import {
  getAdministrationSelfContext,
  getServiceOperationsCapabilities,
} from "../../backend/functions";
import { ProtectedAndroidPushGovernance } from "../android-push-governance/panel";
import { ConnectedIosPushGovernance } from "./ios-push-policy";
import { ProtectedEmailGovernance } from "../email-governance/panel";
import { ProtectedWhatsAppGovernance } from "../whatsapp-governance/panel";
import { ProtectedDeliveryEvidence } from "./delivery-evidence";
import { ProtectedDeliveryDiagnostics } from "./delivery-diagnostics";
import { ProtectedServiceOperations } from "./service-operations";
import { ProtectedEvaluationRecovery } from "./evaluation-recovery";
import { ProtectedContainmentAndNotices } from "./containment-notices";
import { ProtectedDataLifecycleAdministration } from "../data-lifecycle/panel";
import {
  AdministrationAuditDetails,
  AdministrationImpactPanel,
  AdministrationPageHeader,
  AdministrationSection,
  AdministrationState,
  AdministrationStatusSummary,
} from "../../ui/administration-surface";
import { exactUtcTimestamp, formatAdministrationTime } from "../../ui/administration-time";

function newCorrelationId(): string {
  if (typeof globalThis.crypto.randomUUID !== "function") {
    throw new Error("A secure correlation generator is unavailable in this browser.");
  }
  return `corr_${globalThis.crypto.randomUUID().replaceAll("-", "")}`;
}

function isRecord(value: unknown): value is Readonly<Record<string, unknown>> {
  return typeof value === "object" && value !== null;
}

export function isAdministrationSecurityFailure(error: unknown): boolean {
  if (!isRecord(error) || !isRecord(error["data"])) return false;
  return [
    "AUTHENTICATION_REQUIRED",
    "AUTHORIZATION_DENIED",
    "REAUTHENTICATION_REQUIRED",
    "MFA_REQUIRED",
  ].includes(String(error["data"]["code"]));
}

export function OperationalContextLoading(): React.JSX.Element {
  return (
    <AdministrationState
      state="loading"
      title="Preparing operations overview"
      message="Checking the latest aggregate service evidence."
    />
  );
}

export function OperationalContextFailure(): React.JSX.Element {
  return (
    <AdministrationState
      state="error"
      title="Operations overview unavailable"
      message="No household reminder information has been shown. Refresh the page to try the protected aggregate read again."
    />
  );
}

type OperationalContextErrorBoundaryProps = Readonly<{ children: ReactNode }>;
type OperationalContextErrorBoundaryState = Readonly<{ error?: unknown; hasError: boolean }>;

export class OperationalContextErrorBoundary extends Component<
  OperationalContextErrorBoundaryProps,
  OperationalContextErrorBoundaryState
> {
  public override state: OperationalContextErrorBoundaryState = { hasError: false };

  public static getDerivedStateFromError(error: unknown): OperationalContextErrorBoundaryState {
    return { error, hasError: true };
  }

  public override render(): ReactNode {
    if (!this.state.hasError) return this.props.children;
    if (isAdministrationSecurityFailure(this.state.error)) throw this.state.error;
    return <OperationalContextFailure />;
  }
}

export function ProtectedOperationalContextScreen({
  page,
}: Readonly<{ page: AdministrationPageId }>): React.JSX.Element {
  if (page === "service-health") return <ProtectedGoal5Operations page={page} />;
  if (page === "delivery-services")
    return (
      <>
        <ProtectedDeliveryEvidence />
        <ProtectedNotificationHours />
        <ProtectedAndroidPushGovernance />
        <OperationalContextErrorBoundary>
          <ConnectedIosPushGovernance />
        </OperationalContextErrorBoundary>
        <ProtectedEmailGovernance />
        <ProtectedWhatsAppGovernance />
        <ProtectedDeliveryDiagnostics />
      </>
    );
  if (page === "evaluation-recovery") return <ProtectedGoal5Operations page={page} />;
  if (page === "data-lifecycle") return <ProtectedDataLifecycleAdministration />;
  return <ProtectedOperationsOverview />;
}

function ProtectedOperationsOverview(): React.JSX.Element {
  const [correlationId] = useState(newCorrelationId);
  const context = useQuery(getAdministrationSelfContext, { correlationId });
  if (context === undefined) return <OperationalContextLoading />;
  return <ProtectedOperationalContextView context={context} />;
}

function ProtectedGoal5Operations({
  page,
}: Readonly<{ page: "service-health" | "evaluation-recovery" }>): React.JSX.Element | null {
  const [correlationId] = useState(newCorrelationId);
  const result = useQuery(getServiceOperationsCapabilities, { correlationId });
  if (result === undefined)
    return (
      <AdministrationState
        state="loading"
        title={
          page === "service-health" ? "Preparing service health" : "Preparing evaluation recovery"
        }
        message="Checking your current operational permissions."
      />
    );
  const granted = (capability: string) =>
    result.capabilities.some((item) => item.capability === capability && item.granted);
  const anyContainmentOrNotice =
    granted("operations:restrict") ||
    granted("operations:security-lock") ||
    granted("operations:publish-notice");
  if (
    !granted("operations:read-service") &&
    !anyContainmentOrNotice &&
    !granted("operations:recover-evaluator")
  )
    return (
      <AdministrationState
        state="empty"
        title="This work area is not available"
        message="Your current administration access does not include this operation."
      />
    );
  return (
    <>
      {page === "service-health" &&
      !granted("operations:read-service") &&
      anyContainmentOrNotice ? (
        <AdministrationPageHeader
          title="Service health"
          purpose="Review the service controls available to you without exposing customer reminder content."
          status={{ label: "Protected controls", tone: "monitor" }}
        />
      ) : null}
      {page === "service-health" && granted("operations:read-service") ? (
        <ProtectedServiceOperations />
      ) : null}
      {page === "service-health" && anyContainmentOrNotice ? (
        <ProtectedContainmentAndNotices />
      ) : null}
      {page === "evaluation-recovery" && granted("operations:recover-evaluator") ? (
        <ProtectedEvaluationRecovery />
      ) : null}
    </>
  );
}

export function ProtectedOperationalContextView({
  context,
}: Readonly<{ context: AdministrationSelfContext }>): React.JSX.Element {
  const processingStarted = context.occurrenceEvaluation.state === "completed";
  return (
    <>
      <AdministrationPageHeader
        title="What needs your attention"
        purpose="Start with service impact and the next safe action. Aggregate activity and exact audit evidence follow."
        meta={<span className="environment-badge">Shared Test Â· synthetic only</span>}
      />
      <AdministrationStatusSummary
        items={[
          {
            label: "Needs action",
            tone: "action",
            title: processingStarted
              ? "No processing action indicated"
              : "Scheduled processing has not completed",
            description: processingStarted
              ? "The latest aggregate checkpoint does not indicate a processing intervention."
              : "Check service health before relying on scheduled reminder processing.",
          },
          {
            label: "Monitor",
            tone: "monitor",
            title: "Delivery evidence is separate",
            description:
              "A processing checkpoint does not prove that a channel delivered a reminder.",
          },
          {
            label: "Healthy",
            tone: "healthy",
            title: processingStarted ? "Processing checkpoint available" : "Aggregate access ready",
            description: processingStarted
              ? "Scheduled processing is recorded through a known point in time."
              : "Protected aggregate activity is available without customer reminder content.",
          },
        ]}
      />
      <AdministrationImpactPanel
        label="Privacy boundary"
        title="Customer reminder details stay private"
        description="Administrators cannot view reminder names, dates, notes or other reminder content."
        action={
          <a className="secondary-action" href="#operations/service-health">
            Review service health
          </a>
        }
        tone="healthy"
      />
      <section aria-label="Protected aggregate summary" className="summary">
        <div className="measure">
          <strong>{context.householdCount}</strong>
          <span>Active households</span>
        </div>
        <div className="measure">
          <strong>{context.adultCount}</strong>
          <span>Active adults</span>
        </div>
        <div className="measure">
          <strong>{context.trackedRenewalCount}</strong>
          <span>Tracked renewals</span>
        </div>
        <div className="measure">
          <strong>{context.reminderOccurrenceCount}</strong>
          <span>Reminder occurrences recorded</span>
        </div>
      </section>
      <AdministrationSection
        id="scheduler-assurance-title"
        title="Scheduled reminder processing"
        description="Shows how far the scheduled evaluator has progressed. This is not delivery or customer-completion evidence."
        className="scheduler-assurance"
      >
        {context.occurrenceEvaluation.state === "not-started" ? (
          <div className="scheduler-empty">
            <strong className="state review">Not started</strong>
            <p>No scheduled reminder check has completed in this environment yet.</p>
          </div>
        ) : (
          <div className="scheduler-progress">
            <strong className="state">Up to date</strong>
            <p>
              Processing is up to date through{" "}
              <time
                dateTime={exactUtcTimestamp(
                  context.occurrenceEvaluation.evaluatedThroughEpochMilliseconds,
                )}
              >
                {formatAdministrationTime(
                  context.occurrenceEvaluation.evaluatedThroughEpochMilliseconds,
                )}
              </time>
              .
            </p>
          </div>
        )}
      </AdministrationSection>
      <AdministrationAuditDetails summary="Environment, policy, checkpoint and correlation evidence">
        <dl className="audit-register">
          <div>
            <dt>Environment</dt>
            <dd>{context.environment}</dd>
          </div>
          <div>
            <dt>Data policy</dt>
            <dd>{context.dataPolicy}</dd>
          </div>
          {context.occurrenceEvaluation.state === "completed" ? (
            <>
              <div>
                <dt>Evaluated through (UTC)</dt>
                <dd>
                  <time
                    dateTime={exactUtcTimestamp(
                      context.occurrenceEvaluation.evaluatedThroughEpochMilliseconds,
                    )}
                  >
                    {exactUtcTimestamp(
                      context.occurrenceEvaluation.evaluatedThroughEpochMilliseconds,
                    )}
                  </time>
                </dd>
              </div>
              <div>
                <dt>Checkpoint revision</dt>
                <dd className="number">{context.occurrenceEvaluation.checkpointRevision}</dd>
              </div>
              <div>
                <dt>Recorded (UTC)</dt>
                <dd>
                  <time
                    dateTime={exactUtcTimestamp(
                      context.occurrenceEvaluation.updatedAtEpochMilliseconds,
                    )}
                  >
                    {exactUtcTimestamp(context.occurrenceEvaluation.updatedAtEpochMilliseconds)}
                  </time>
                </dd>
              </div>
            </>
          ) : null}
          <div>
            <dt>Read outcome</dt>
            <dd>Authorised aggregate read</dd>
          </div>
          <div>
            <dt>Correlation reference</dt>
            <dd className="masked">{context.correlationId}</dd>
          </div>
        </dl>
      </AdministrationAuditDetails>
    </>
  );
}
